import {
  deleteWallRevision,
  fetchWallAdmin,
  publishWallDraft,
  restoreWallRevision,
  saveWallDraft,
} from "../lib/cloud";
import { renderBlockEditorForm } from "./wall/block-editor";
import { renderWallBlocks } from "./wall/render-blocks";
import { blockFactPool, pickFromFactPool, WALL_DEFAULT_FACTS } from "./wall/default-facts";
import {
  findStackItem,
  flattenWallBlocks,
  isStackBlock,
  mergeWallBlocks,
  patchStackItem,
  reorderWallBlocks,
  splitStack,
  stackItems,
  type StackInsert,
  unstackItem,
  type WallRootBlock,
} from "./wall/stack-utils";
import {
  createBlock,
  type WallBlock,
  type WallBlockType,
  type WallLiveData,
  wallBlockLabel,
  WALL_BLOCK_TYPES,
} from "./wall/types";

const AUTOSAVE_MS = 2000;
const MAX_UNDO = 50;
const DRAG_THRESHOLD_PX = 10;

type WallEditorDragSession =
  | {
      kind: "row";
      blockId: string;
      pointerId: number;
      startX: number;
      startY: number;
      active: boolean;
      sourceEl: HTMLElement;
    }
  | {
      kind: "unstack";
      stackId: string;
      itemId: string;
      stackRowEl: HTMLElement;
      pointerId: number;
      startX: number;
      startY: number;
      active: boolean;
      sourceEl: HTMLElement;
    };

function buildPreviewLive(blocks: WallRootBlock[]): WallLiveData {
  const randomFacts: Record<string, string> = {};
  for (const b of flattenWallBlocks(blocks).filter((x) => x.type === "randomFact")) {
    const custom = blockFactPool(b.data);
    const pool = custom.length ? custom : WALL_DEFAULT_FACTS;
    randomFacts[b.id] = pickFromFactPool(pool, `preview-${b.id}`);
  }
  return {
    counters: {},
    progress: {},
    polls: {},
    stats: {
      totalGames: 12840,
      totalDeaths: 502100,
      totalPoints: 987654321,
      totalUsers: 420,
      totalPlaytimeSec: 864000,
    },
    spotlight: null,
    featuredPresets: [],
    featuredSpeedruns: [],
    randomFacts,
  };
}

export function mountAdminWallEditor(mount: HTMLElement): { refresh: () => void } {
  mount.className = "adminWallEditor";
  mount.innerHTML = "";

  const toolbar = document.createElement("div");
  toolbar.className = "adminWallToolbar";

  const btnUndo = mkBtn("Undo", "btn");
  const btnRedo = mkBtn("Redo", "btn");
  const btnPublish = mkBtn("Publish", "btn green");
  const btnAdd = mkBtn("+ Block", "btn");
  const status = document.createElement("span");
  status.className = "adminWallStatus accessHint";
  status.textContent = "Drag blocks to reorder or stack; drag a section chip outside its card to unstack.";

  toolbar.append(btnUndo, btnRedo, btnPublish, btnAdd, status);
  mount.appendChild(toolbar);

  const layout = document.createElement("div");
  layout.className = "adminWallLayout";

  const listCol = document.createElement("div");
  listCol.className = "adminWallListCol";
  const blockList = document.createElement("div");
  blockList.className = "adminWallBlockList";
  listCol.appendChild(blockList);

  const editorCol = document.createElement("div");
  editorCol.className = "adminWallEditorCol";
  const editorPanel = document.createElement("div");
  editorPanel.className = "adminWallEditorPanel";
  editorCol.appendChild(editorPanel);

  const previewCol = document.createElement("div");
  previewCol.className = "adminWallPreviewCol";
  const previewTitle = document.createElement("h3");
  previewTitle.textContent = "Preview";
  const previewScroll = document.createElement("div");
  previewScroll.className = "adminWallPreviewScroll";
  const previewMount = document.createElement("div");
  previewMount.className = "wallBlocks wallBlocks--preview";
  previewScroll.appendChild(previewMount);
  previewCol.append(previewTitle, previewScroll);

  const historyCol = document.createElement("div");
  historyCol.className = "adminWallHistoryCol";
  const historyTitle = document.createElement("h3");
  historyTitle.textContent = "Publish history";
  const historyList = document.createElement("ul");
  historyList.className = "adminWallHistoryList";
  historyCol.append(historyTitle, historyList);

  layout.append(listCol, editorCol, previewCol, historyCol);
  mount.appendChild(layout);

  let blocks: WallRootBlock[] = [];
  let selectedId: string | null = null;
  let selectedSubId: string | null = null;
  let permissions = { edit: false, publish: false, delete: false };
  const undoStack: WallRootBlock[][] = [];
  const redoStack: WallRootBlock[][] = [];
  let autosaveTimer: number | null = null;
  let dragId: string | null = null;
  let dragSession: WallEditorDragSession | null = null;
  let dragGhost: HTMLElement | null = null;

  function clearDragHighlights() {
    blockList.querySelectorAll(".adminWallDropGap--active").forEach((el) => {
      el.classList.remove("adminWallDropGap--active");
    });
    blockList.querySelectorAll(".adminWallBlockRow--joinAbove, .adminWallBlockRow--joinBelow").forEach((el) => {
      el.classList.remove("adminWallBlockRow--joinAbove", "adminWallBlockRow--joinBelow");
    });
    blockList.querySelectorAll(".adminWallBlockRowStack--unstackReady").forEach((el) => {
      el.classList.remove("adminWallBlockRowStack--unstackReady");
    });
  }

  function destroyDragGhost() {
    dragGhost?.remove();
    dragGhost = null;
  }

  function createDragGhost(label: string) {
    destroyDragGhost();
    const ghost = document.createElement("div");
    ghost.className = "adminWallDragGhost";
    ghost.textContent = label;
    document.body.appendChild(ghost);
    dragGhost = ghost;
    return ghost;
  }

  function moveDragGhost(clientX: number, clientY: number) {
    if (!dragGhost) return;
    dragGhost.style.left = `${clientX}px`;
    dragGhost.style.top = `${clientY}px`;
  }

  function isInsideRect(x: number, y: number, rect: DOMRect, pad = 0): boolean {
    return (
      x >= rect.left - pad &&
      x <= rect.right + pad &&
      y >= rect.top - pad &&
      y <= rect.bottom + pad
    );
  }

  function resolveRowDropTarget(
    clientX: number,
    clientY: number,
  ): { type: "gap"; index: number } | { type: "row"; blockId: string; insert: StackInsert } | null {
    const elements = document.elementsFromPoint(clientX, clientY);
    for (const el of elements) {
      if (!(el instanceof HTMLElement)) continue;
      if (el.classList.contains("adminWallDropGap")) {
        const idx = Number.parseInt(el.dataset.gapIndex ?? "-1", 10);
        if (idx >= 0) return { type: "gap", index: idx };
      }
      const row = el.closest(".adminWallBlockRow");
      if (row instanceof HTMLElement && row.dataset.blockId) {
        const rect = row.getBoundingClientRect();
        const insert: StackInsert = clientY < rect.top + rect.height / 2 ? "above" : "below";
        return { type: "row", blockId: row.dataset.blockId, insert };
      }
    }
    return null;
  }

  function updateRowDragHighlights(clientX: number, clientY: number) {
    clearDragHighlights();
    if (!dragId) return;
    const target = resolveRowDropTarget(clientX, clientY);
    if (!target) return;
    if (target.type === "gap") {
      const gap = blockList.querySelector(`.adminWallDropGap[data-gap-index="${target.index}"]`);
      gap?.classList.add("adminWallDropGap--active");
      return;
    }
    if (target.blockId === dragId) return;
    const row = blockList.querySelector(`.adminWallBlockRow[data-block-id="${target.blockId}"]`);
    if (!row) return;
    row.classList.toggle("adminWallBlockRow--joinAbove", target.insert === "above");
    row.classList.toggle("adminWallBlockRow--joinBelow", target.insert === "below");
  }

  function finishRowDrag(clientX: number, clientY: number) {
    if (!dragId) return;
    const target = resolveRowDropTarget(clientX, clientY);
    if (!target) return;
    if (target.type === "gap") {
      setBlocks(reorderWallBlocks(blocks, dragId, target.index));
      return;
    }
    if (target.blockId !== dragId) {
      setBlocks(mergeWallBlocks(blocks, dragId, target.blockId, target.insert));
    }
  }

  function finishUnstackDrag(clientX: number, clientY: number, session: Extract<WallEditorDragSession, { kind: "unstack" }>) {
    const rect = session.stackRowEl.getBoundingClientRect();
    if (isInsideRect(clientX, clientY, rect)) return;
    setBlocks(unstackItem(blocks, session.stackId, session.itemId));
    if (selectedSubId === session.itemId) {
      selectedSubId = null;
      selectedId = session.itemId;
    }
  }

  function endDragSession(clientX: number, clientY: number) {
    const session = dragSession;
    if (!session) return;

    if (session.active) {
      if (session.kind === "row") finishRowDrag(clientX, clientY);
      else finishUnstackDrag(clientX, clientY, session);
    }

    session.sourceEl.classList.remove(
      "adminWallBlockRow--drag",
      "adminWallBlockRowStackItem--drag",
    );
    clearDragHighlights();
    destroyDragGhost();
    dragSession = null;
    dragId = null;
  }

  function onWallEditorPointerMove(e: PointerEvent) {
    const session = dragSession;
    if (!session || e.pointerId !== session.pointerId) return;

    const dx = e.clientX - session.startX;
    const dy = e.clientY - session.startY;
    if (!session.active && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

    if (!session.active) {
      session.active = true;
      session.sourceEl.classList.add(
        session.kind === "row" ? "adminWallBlockRow--drag" : "adminWallBlockRowStackItem--drag",
      );
      const label =
        session.kind === "row"
          ? wallBlockLabel(blocks.find((b) => b.id === session.blockId)?.type ?? "text")
          : wallBlockLabel(findStackItem(blocks, session.itemId)?.item.type ?? "text");
      createDragGhost(label);
      if (session.kind === "row") dragId = session.blockId;
    }

    e.preventDefault();
    moveDragGhost(e.clientX, e.clientY);

    if (session.kind === "row") {
      updateRowDragHighlights(e.clientX, e.clientY);
      return;
    }

    const stackEl = session.stackRowEl.querySelector(".adminWallBlockRowStack");
    const rect = session.stackRowEl.getBoundingClientRect();
    stackEl?.classList.toggle("adminWallBlockRowStack--unstackReady", !isInsideRect(e.clientX, e.clientY, rect));
  }

  function onWallEditorPointerUp(e: PointerEvent) {
    const session = dragSession;
    if (!session || e.pointerId !== session.pointerId) return;

    if (!session.active) {
      if (session.kind === "unstack") {
        selectedId = session.stackId;
        selectedSubId = session.itemId;
        renderList();
        renderEditor();
      } else {
        selectedId = session.blockId;
        selectedSubId = null;
        renderList();
        renderEditor();
      }
    } else {
      endDragSession(e.clientX, e.clientY);
      return;
    }

    session.sourceEl.classList.remove(
      "adminWallBlockRow--drag",
      "adminWallBlockRowStackItem--drag",
    );
    dragSession = null;
  }

  function onWallEditorPointerCancel(e: PointerEvent) {
    if (!dragSession || e.pointerId !== dragSession.pointerId) return;
    endDragSession(e.clientX, e.clientY);
  }

  function bindRowPointerDrag(row: HTMLElement, blockId: string) {
    row.style.touchAction = "none";
    row.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.closest("button") && !t.closest(".adminWallBlockRowLabel")) return;
      if (t.closest(".adminWallBlockRowStackItem")) return;
      dragSession = {
        kind: "row",
        blockId,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
        sourceEl: row,
      };
      try {
        row.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    });
    row.addEventListener("pointerup", onWallEditorPointerUp);
    row.addEventListener("pointercancel", onWallEditorPointerCancel);
    row.addEventListener("lostpointercapture", (e) => {
      if (dragSession?.pointerId === e.pointerId && dragSession.active) {
        endDragSession(e.clientX, e.clientY);
      }
    });
  }

  function bindUnstackChipDrag(
    chip: HTMLElement,
    stackId: string,
    itemId: string,
    stackRowEl: HTMLElement,
  ) {
    chip.style.touchAction = "none";
    chip.title = "Drag outside the card to unstack";
    chip.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      dragSession = {
        kind: "unstack",
        stackId,
        itemId,
        stackRowEl,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        active: false,
        sourceEl: chip,
      };
      try {
        chip.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    });
    chip.addEventListener("pointerup", onWallEditorPointerUp);
    chip.addEventListener("pointercancel", onWallEditorPointerCancel);
    chip.addEventListener("lostpointercapture", (e) => {
      if (dragSession?.pointerId === e.pointerId && dragSession.active) {
        endDragSession(e.clientX, e.clientY);
      }
    });
  }

  window.addEventListener("pointermove", onWallEditorPointerMove);

  function pushUndo() {
    undoStack.push(JSON.parse(JSON.stringify(blocks)) as WallRootBlock[]);
    if (undoStack.length > MAX_UNDO) undoStack.shift();
    redoStack.length = 0;
  }

  function refreshPreview() {
    renderWallBlocks(
      blocks as WallBlock[],
      { mode: "preview", live: buildPreviewLive(blocks), authenticated: false },
      previewMount,
    );
  }

  function patchBlockData(blockId: string, patch: Record<string, unknown>, opts?: { rerender?: boolean }) {
    blocks = blocks.map((b) => (b.id === blockId ? { ...b, data: { ...b.data, ...patch } } : b));
    scheduleAutosave();
    refreshPreview();
    if (opts?.rerender) renderEditor();
  }

  function patchSubBlockData(itemId: string, patch: Record<string, unknown>, opts?: { rerender?: boolean }) {
    const found = findStackItem(blocks, itemId);
    if (!found) return;
    blocks = patchStackItem(blocks, found.stack.id, itemId, patch);
    scheduleAutosave();
    refreshPreview();
    if (opts?.rerender) renderEditor();
  }

  function setBlocks(next: WallRootBlock[], recordUndo = true) {
    if (recordUndo) pushUndo();
    blocks = next;
    renderList();
    renderEditor();
    refreshPreview();
    scheduleAutosave();
  }

  function scheduleAutosave() {
    if (!permissions.edit) return;
    status.textContent = "Unsaved changes…";
    if (autosaveTimer != null) window.clearTimeout(autosaveTimer);
    autosaveTimer = window.setTimeout(() => void saveDraft(), AUTOSAVE_MS);
  }

  async function saveDraft() {
    if (!permissions.edit) return;
    status.textContent = "Saving…";
    try {
      const r = await saveWallDraft(blocks);
      status.textContent = `Saved ${new Date(r.draftUpdatedAt).toLocaleTimeString()}`;
    } catch {
      status.textContent = "Save failed.";
    }
  }

  function makeDropGap(beforeIndex: number) {
    const gap = document.createElement("div");
    gap.className = "adminWallDropGap";
    gap.dataset.gapIndex = String(beforeIndex);
    gap.title = "Drop here to reorder";
    return gap;
  }

  function renderList() {
    blockList.replaceChildren();
    blockList.appendChild(makeDropGap(0));

    blocks.forEach((b, index) => {
      const row = document.createElement("div");
      row.className = "adminWallBlockRow";
      if (b.id === selectedId && !selectedSubId) row.classList.add("adminWallBlockRow--active");
      if (isStackBlock(b)) row.classList.add("adminWallBlockRow--stack");
      row.dataset.blockId = b.id;

      const label = document.createElement("span");
      label.className = "adminWallBlockRowLabel";
      label.textContent = wallBlockLabel(b.type);
      row.appendChild(label);

      if (isStackBlock(b)) {
        const sub = document.createElement("div");
        sub.className = "adminWallBlockRowStack";
        for (const item of stackItems(b)) {
          const chip = document.createElement("button");
          chip.type = "button";
          chip.className = "adminWallBlockRowStackItem";
          if (selectedSubId === item.id) chip.classList.add("adminWallBlockRowStackItem--active");
          chip.textContent = wallBlockLabel(item.type);
          bindUnstackChipDrag(chip, b.id, item.id, row);
          sub.appendChild(chip);
        }
        const splitAll = mkBtn("Unstack all", "btn adminWallBlockUnstackAll");
        splitAll.onclick = (e) => {
          e.stopPropagation();
          setBlocks(splitStack(blocks, b.id));
          selectedSubId = null;
        };
        sub.appendChild(splitAll);
        row.appendChild(sub);
      }

      const del = mkBtn("×", "btn adminWallBlockDel");
      del.onclick = (e) => {
        e.stopPropagation();
        if (!window.confirm("Delete this block?")) return;
        setBlocks(blocks.filter((x) => x.id !== b.id));
        if (selectedId === b.id) {
          selectedId = null;
          selectedSubId = null;
        }
      };
      row.appendChild(del);

      bindRowPointerDrag(row, b.id);

      blockList.appendChild(row);
      blockList.appendChild(makeDropGap(index + 1));
    });
  }

  function renderEditor() {
    editorPanel.replaceChildren();

    if (selectedSubId) {
      const found = findStackItem(blocks, selectedSubId);
      if (!found) {
        selectedSubId = null;
        renderEditor();
        return;
      }
      const back = mkBtn("← Back to card", "btn");
      back.onclick = () => {
        selectedSubId = null;
        renderEditor();
      };
      editorPanel.append(back);
      editorPanel.appendChild(
        renderBlockEditorForm(found.item, {
          getData: () => findStackItem(blocks, selectedSubId!)?.item.data ?? {},
          patchData: (patch, opts) => {
            if (!selectedSubId) return;
            patchSubBlockData(selectedSubId, patch, opts);
          },
        }),
      );
      return;
    }

    const block = blocks.find((b) => b.id === selectedId);
    if (!block) {
      editorPanel.textContent = "Select a block to edit, or add a new one.";
      return;
    }

    if (isStackBlock(block)) {
      const hint = document.createElement("p");
      hint.className = "accessHint";
      hint.textContent = "Card stack — tap a section to edit it, or drag a chip outside the card to unstack.";
      editorPanel.appendChild(hint);
      const unstackAll = mkBtn("Unstack all", "btn green");
      unstackAll.onclick = () => {
        setBlocks(splitStack(blocks, block.id));
        selectedSubId = null;
      };
      editorPanel.appendChild(unstackAll);
      const list = document.createElement("div");
      list.className = "adminWallStackEditList";
      for (const item of stackItems(block)) {
        const btn = mkBtn(wallBlockLabel(item.type), "btn");
        btn.onclick = () => {
          selectedSubId = item.id;
          renderList();
          renderEditor();
        };
        list.appendChild(btn);
      }
      editorPanel.appendChild(list);
      return;
    }

    editorPanel.appendChild(
      renderBlockEditorForm(block as WallBlock, {
        getData: () => (blocks.find((b) => b.id === selectedId)?.data ?? {}) as Record<string, unknown>,
        patchData: (patch, opts) => {
          if (!selectedId) return;
          patchBlockData(selectedId, patch, opts);
        },
      }),
    );
  }

  function renderHistory(
    history: Array<{ id: string; publishedAt: string; label: string | null }>,
  ) {
    historyList.replaceChildren();
    for (const h of history) {
      const li = document.createElement("li");
      li.className = "adminWallHistoryItem";
      const meta = document.createElement("span");
      meta.className = "adminWallHistoryMeta";
      meta.textContent = `${new Date(h.publishedAt).toLocaleString()}${h.label ? ` — ${h.label}` : ""}`;
      li.appendChild(meta);
      const actions = document.createElement("span");
      actions.className = "adminWallHistoryActions";
      if (permissions.edit) {
        const restore = mkBtn("Restore", "btn green");
        restore.onclick = () => {
          if (
            !window.confirm(
              "Restore this published version into the draft? Your current draft will be replaced (you can still Undo).",
            )
          ) {
            return;
          }
          void (async () => {
            try {
              pushUndo();
              await restoreWallRevision(h.id);
              selectedId = null;
              selectedSubId = null;
              status.textContent = "Draft restored from history.";
              await load();
            } catch {
              status.textContent = "Restore failed.";
            }
          })();
        };
        actions.appendChild(restore);
      }
      if (permissions.delete) {
        const del = mkBtn("Delete", "btn");
        del.onclick = () => {
          void (async () => {
            try {
              await deleteWallRevision(h.id);
              status.textContent = "Revision deleted.";
              await load();
            } catch {
              status.textContent = "Delete failed.";
            }
          })();
        };
        actions.appendChild(del);
      }
      li.appendChild(actions);
      historyList.appendChild(li);
    }
  }

  btnUndo.onclick = () => {
    const prev = undoStack.pop();
    if (!prev) return;
    redoStack.push(JSON.parse(JSON.stringify(blocks)) as WallRootBlock[]);
    blocks = prev;
    renderList();
    renderEditor();
    refreshPreview();
    scheduleAutosave();
  };

  btnRedo.onclick = () => {
    const next = redoStack.pop();
    if (!next) return;
    undoStack.push(JSON.parse(JSON.stringify(blocks)) as WallRootBlock[]);
    blocks = next;
    renderList();
    renderEditor();
    refreshPreview();
    scheduleAutosave();
  };

  btnPublish.onclick = () => {
    if (!permissions.publish) return;
    const label = window.prompt("Publish label (optional):") ?? "";
    void (async () => {
      status.textContent = "Publishing…";
      try {
        await saveWallDraft(blocks);
        await publishWallDraft(label || undefined);
        status.textContent = "Published!";
        await load();
      } catch {
        status.textContent = "Publish failed.";
      }
    })();
  };

  btnAdd.onclick = () => {
    const menu = document.createElement("div");
    menu.className = "adminWallAddMenu";
    for (const type of WALL_BLOCK_TYPES) {
      const b = mkBtn(wallBlockLabel(type), "btn");
      b.onclick = () => {
        const block = createBlock(type as WallBlockType);
        pushUndo();
        blocks = [...blocks, block];
        selectedId = block.id;
        selectedSubId = null;
        menu.remove();
        renderList();
        renderEditor();
        refreshPreview();
        scheduleAutosave();
      };
      menu.appendChild(b);
    }
    mount.appendChild(menu);
    window.setTimeout(() => {
      const close = (e: MouseEvent) => {
        if (!menu.contains(e.target as Node)) {
          menu.remove();
          document.removeEventListener("click", close);
        }
      };
      document.addEventListener("click", close);
    }, 0);
  };

  async function load() {
    status.textContent = "Loading…";
    try {
      const data = await fetchWallAdmin();
      blocks = (data.draftBlocks ?? []) as WallRootBlock[];
      permissions = data.permissions ?? permissions;
      btnPublish.classList.toggle("hidden", !permissions.publish);
      btnAdd.classList.toggle("hidden", !permissions.edit);
      renderHistory(data.history ?? []);
      renderList();
      renderEditor();
      refreshPreview();
      status.textContent = "Ready — drag to reorder/stack; drag a chip outside its card to unstack.";
    } catch {
      status.textContent = "Load failed (forbidden or offline).";
    }
  }

  void load();

  return { refresh: () => void load() };
}

function mkBtn(text: string, cls: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = cls;
  b.textContent = text;
  return b;
}
